<template>
    <div class="activity-data-viewer-compact">
        <!-- 필터 섹션 -->
        <div class="filter-section-compact">
            <div class="section-header-compact">
                <h3 v-if="fixedPeriod" class="filter-title">활동자료 수집 설정</h3>
                <div class="filter-right-group">
                    <div class="filter-fields-group">
                        <!-- <input
                        v-model="filters.period"
                        type="text"
                        class="input-compact"
                        placeholder="기간 (YYYYMM)"
                        maxlength="6"
                        @keyup.enter="loadData"
                    /> -->
                        <template v-if="fixedPeriod">
                            <span class="period-fixed-label">{{ formatPeriod(fixedPeriod) }}</span>
                        </template>
                        <CarbonSelect
                            v-if="!fixedOrgIdx"
                            :value="filters.orgIdx == null ? '' : filters.orgIdx"
                            :options="orgSelectOptions"
                            placeholder="전체 조직"
                            :placeholderDisabled="false"
                            @input="
                                (v) => {
                                    filters.orgIdx = v === '' ? null : v;
                                    loadData();
                                }
                            "
                        />
                        <DatePicker
                            v-else
                            v-model="periodFormatted"
                            type="month"
                            format="YYYY-MM"
                            value-type="format"
                            placeholder="연월 선택"
                            class="period-picker"
                            :clearable="false"
                            @change="loadData"
                        />
                        <CarbonSelect
                            :value="filters.scope == null ? '' : filters.scope"
                            :options="scopeOptions"
                            placeholder="전체 Scope"
                            :placeholderDisabled="false"
                            @input="
                                (v) => {
                                    filters.scope = v === '' ? null : v;
                                    loadData();
                                }
                            "
                        />
                        <CarbonSelect
                            :value="filters.facilityIdx == null ? '' : filters.facilityIdx"
                            :options="facilitySelectOptions"
                            placeholder="전체 시설"
                            :placeholderDisabled="false"
                            @input="
                                (v) => {
                                    filters.facilityIdx = v === '' ? null : v;
                                    loadData();
                                }
                            "
                        />
                        <CarbonSelect
                            :value="filters.categoryIdx == null ? '' : filters.categoryIdx"
                            :options="categorySelectOptions"
                            placeholder="전체 카테고리"
                            :placeholderDisabled="false"
                            @input="
                                (v) => {
                                    filters.categoryIdx = v === '' ? null : v;
                                    loadData();
                                }
                            "
                        />
                        <CarbonSelect
                            :value="filters.draftStat == null ? '' : filters.draftStat"
                            :options="draftStatOptions"
                            placeholder="전체 상태"
                            :placeholderDisabled="false"
                            @input="
                                (v) => {
                                    filters.draftStat = v === '' ? null : v;
                                    loadData();
                                }
                            "
                        />
                        <CarbonButton icon="fas fa-redo" @click="resetFilters">초기화</CarbonButton>
                        <CarbonButton variant="primary" icon="fas fa-search" @click="loadData">조회</CarbonButton>
                    </div>
                    <div v-if="!fixedPeriod" class="filter-actions">
                        <!-- <button class="btn-compact btn-primary" @click="exportData">
                        <i class="fas fa-download"></i>
                        내보내기
                    </button> -->
                    </div>
                </div>
            </div>
        </div>

        <!-- 데이터 테이블 -->
        <div class="section-block">
            <div class="table-container-compact">
                <table class="table-compact">
                    <thead>
                        <tr>
                            <th style="text-align: center">기간</th>
                            <th style="text-align: center">시설명</th>
                            <th style="text-align: center">Scope</th>
                            <th style="text-align: center">카테고리</th>
                            <th class="th-amount" style="text-align: right">활동량</th>
                            <th style="text-align: right">단위</th>
                            <th style="text-align: center">상태</th>
                            <th style="text-align: center"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="8" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</td>
                        </tr>
                        <tr v-else-if="activityData.length === 0">
                            <td colspan="8" class="text-center empty">조회된 데이터가 없습니다</td>
                        </tr>
                        <tr v-else v-for="row in paginatedData" :key="row.activityIdx">
                            <td style="text-align: center">{{ formatPeriod(row.period) }}</td>
                            <td style="text-align: center">{{ getFacilityName(row.facilityIdx) || "-" }}</td>
                            <td style="text-align: center">
                                <span>Scope {{ row.scope }}</span>
                            </td>
                            <td style="text-align: center">{{ row.category }}</td>
                            <td
                                style="text-align: right"
                                class="amount-cell"
                                :class="{
                                    'is-editing': editingAmountRow === row.activityIdx,
                                    'is-disabled': row.sourceType === 'ALLOCATED',
                                }"
                                :title="
                                    row.sourceType === 'ALLOCATED'
                                        ? '분배된 데이터는 직접 수정할 수 없습니다'
                                        : '클릭하여 활동량 수정'
                                "
                                @click="startAmountEdit(row)"
                            >
                                <template v-if="editingAmountRow === row.activityIdx">
                                    <input
                                        ref="amountInput"
                                        v-model="editingAmountValue"
                                        type="number"
                                        step="any"
                                        min="0"
                                        class="amount-input-inline"
                                        @blur="handleAmountBlur"
                                        @keydown.enter="handleAmountSave(row)"
                                        @keydown.esc="cancelAmountEdit"
                                    />
                                </template>
                                <template v-else>
                                    <span class="amount-display">{{ formatNumber(row.amount) }}</span>
                                    <span v-if="savingAmountIdx === row.activityIdx" class="amount-saving">
                                        <i class="fas fa-spinner fa-spin"></i>
                                    </span>
                                </template>
                            </td>
                            <td style="text-align: right">{{ row.unit }}</td>

                            <td style="text-align: center">
                                <span class="tag-small" :class="`status-${row.draftStat}`">
                                    {{ getDraftStatLabel(row.draftStat) }}
                                </span>
                            </td>
                            <td style="text-align: center">
                                <div class="action-buttons">
                                    <IconButton icon="fas fa-edit" size="small" title="수정" @click="editRow(row)" />
                                    <IconButton
                                        icon="fas fa-paperclip"
                                        size="small"
                                        title="증빙"
                                        @click="openEvidenceModal(row)"
                                    />
                                    <IconButton
                                        icon="fas fa-trash"
                                        size="small"
                                        variant="delete"
                                        title="삭제"
                                        @click="deleteRow(row)"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <TablePagination
                v-if="totalCount > 0"
                :total-items="totalCount"
                :current-page.sync="currentPage"
                :page-size.sync="perPage"
            />
        </div>

        <!-- 편집 모달 -->
        <EditRowModal
            :visible="showEditModal"
            :mode="editMode"
            :row-data="editingRow"
            :login-user="loginUser"
            :fixed-period="fixedPeriod"
            :fixed-org-idx="fixedOrgIdx"
            :on-save="handleEditSave"
            @close="showEditModal = false"
        />

        <!-- 증빙 모달 -->
        <EvidenceModal
            :visible="showEvidenceModal"
            :row="evidenceRow"
            :facilities="facilities"
            @close="showEvidenceModal = false"
        />
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "@/submodules/carbon-plugin/codes";
import IconButton from "../../common/IconButton.vue";
import TablePagination from "../../common/TablePagination.vue";
import EditRowModal from "../modals/EditRowModal.vue";
import EvidenceModal from "../modals/EvidenceModal.vue";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonButton from "../../common/CarbonButton.vue";
import CarbonSelect from "../../common/CarbonSelect.vue";

export default {
    name: "ActivityDataViewer",
    components: {
        IconButton,
        TablePagination,
        EditRowModal,
        EvidenceModal,
        DatePicker,
        CarbonButton,
        CarbonSelect,
    },
    props: {
        loginUser: {
            type: Object,
            default: () => ({}),
        },
        /** 스텝 컨텍스트: 기간 고정 (YYYYMM) */
        fixedPeriod: {
            type: [String, Number],
            default: null,
        },
        /** 스텝 컨텍스트: 조직 고정 (orgIdx) */
        fixedOrgIdx: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            // 필터
            filters: {
                period: "",
                orgIdx: null,
                scope: null,
                facilityIdx: null,
                categoryIdx: null,
                draftStat: null,
            },

            // 데이터
            activityData: [],
            totalCount: 0,
            currentPage: 1,
            perPage: 10,
            loading: false,

            // 시설 & 카테고리
            facilities: [],
            facilityMap: {},
            categories: [],
            categoryMap: {},
            organizations: [],

            // 편집 모달
            showEditModal: false,
            editMode: "edit",
            editingRow: null,

            // 증빙 모달
            showEvidenceModal: false,
            evidenceRow: null,

            // 활동량 인라인 편집
            editingAmountRow: null,
            editingAmountValue: "",
            savingAmountIdx: null,

            // Config (create 시 configIdx 해석용)
            configs: [],
        };
    },
    computed: {
        draftStatOptions() {
            return CarbonCodes.draftStat.getList();
        },
        scopeOptions() {
            return [
                { value: 1, text: "Scope 1 (직접배출)" },
                { value: 2, text: "Scope 2 (간접배출-전기)" },
                { value: 3, text: "Scope 3 (기타간접배출)" },
            ];
        },
        facilitySelectOptions() {
            return this.facilities.map((f) => ({
                value: f.facilityIdx,
                text: `${f.facilityCode || ""} - ${f.facilityName || ""}`.trim(),
            }));
        },
        orgSelectOptions() {
            return this.organizations.map((org) => ({
                value: org.orgIdx,
                text: `${org.orgCode || ""} - ${org.orgName || ""}`.trim(),
            }));
        },
        categorySelectOptions() {
            return this.categories.map((c) => ({ value: c.categoryIdx, text: c.categoryName }));
        },
        // 클라이언트 사이드 페이지네이션
        paginatedData() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.activityData.slice(start, end);
        },
        // 기간 포맷 변환 (YYYYMM <-> YYYY-MM)
        periodFormatted: {
            get() {
                const period = this.fixedPeriod || this.filters.period;
                if (!period) return "";
                const str = String(period);
                if (str.length === 6) {
                    return `${str.slice(0, 4)}-${str.slice(4, 6)}`;
                }
                return str;
            },
            set(value) {
                if (!value) {
                    this.filters.period = "";
                    return;
                }
                // YYYY-MM -> YYYYMM
                this.filters.period = value.replace(/-/g, "");
            },
        },
    },
    watch: {
        fixedPeriod: {
            handler(val) {
                if (val) {
                    this.filters.period = String(val).replace(/-/g, "");
                    this.loadData();
                }
            },
            immediate: true,
        },
    },
    mounted() {
        this.loadOrganizations();
        this.loadFacilities();
        this.loadCategories();
        if (this.fixedOrgIdx) {
            this.loadConfigs();
        }
        this.loadData();
    },
    methods: {
        async loadOrganizations() {
            try {
                const response = await carbonApi.organization.getOrgs();
                const data = response?.data?.data || response?.data;
                this.organizations = data?.organizations || [];
            } catch (error) {
                console.error("[ActivityDataViewer] 조직 목록 로드 실패:", error);
                this.organizations = [];
            }
        },

        async loadFacilities() {
            try {
                const response = await carbonApi.utility.getFacilities();
                const data = response?.data?.data || response?.data;
                this.facilities = data?.facilities || [];

                this.facilityMap = {};
                this.facilities.forEach((f) => {
                    if (f.facilityCode) {
                        this.facilityMap[f.facilityCode] = f.facilityIdx;
                    }
                });
            } catch (error) {
                console.error("[ActivityDataViewer] 시설 목록 로드 실패:", error);
            }
        },

        async loadCategories() {
            try {
                const response = await carbonApi.operBoundary.getAllCategories();
                const data = response?.data?.data || response?.data;
                this.categories = data?.categories || [];

                this.categoryMap = {};
                this.categories.forEach((c) => {
                    if (c.categoryName) {
                        this.categoryMap[c.categoryName] = c.categoryIdx;
                    }
                });
            } catch (error) {
                console.error("[ActivityDataViewer] 카테고리 목록 로드 실패:", error);
            }
        },

        async loadConfigs() {
            if (!this.fixedOrgIdx) return;
            try {
                const response = await carbonApi.activityConfig.getConfigs({
                    orgIdx: this.fixedOrgIdx,
                    draftStat: "ACTIVE",
                });
                const data = response?.data?.data || response?.data;
                this.configs = Array.isArray(data) ? data : data?.configs || [];
            } catch (error) {
                console.error("[ActivityDataViewer] Config 로드 실패:", error);
                this.configs = [];
            }
        },

        async loadData() {
            this.loading = true;
            try {
                const params = {};

                // fixedPeriod/fixedOrgIdx 우선 (스텝 컨텍스트)
                const period = this.fixedPeriod || this.filters.period;
                if (period) params.period = String(period).replace(/-/g, "");
                if (this.fixedOrgIdx) params.orgIdx = this.fixedOrgIdx;
                else if (this.filters.orgIdx) params.orgIdx = this.filters.orgIdx;

                // 필터 적용 (백엔드에서 전체 데이터를 가져옴)
                if (this.filters.scope) params.scope = this.filters.scope;
                if (this.filters.facilityIdx) params.facilityIdx = this.filters.facilityIdx;

                // ✅ categoryIdx → category (이름) 변환
                if (this.filters.categoryIdx) {
                    const category = this.categories.find((c) => c.categoryIdx === this.filters.categoryIdx);
                    if (category) params.category = category.categoryName;
                }

                // ✅ draftStat 필터 추가
                if (this.filters.draftStat) params.draftStat = this.filters.draftStat;

                const response = await carbonApi.activityData.getActivityData(params);
                const data = response?.data?.data || response?.data;

                // 전체 데이터 저장 (클라이언트 사이드 페이지네이션)
                this.activityData = data?.activityData || [];
                this.totalCount = this.activityData.length;

                // 첫 페이지로 이동
                this.currentPage = 1;

                // 부모에게 데이터 로드 완료 이벤트 발행
                this.$emit("data-loaded", {
                    count: this.totalCount,
                    activityData: this.activityData,
                });
            } catch (error) {
                console.error("[ActivityDataViewer] 데이터 로드 실패:", error);
                this.showToast("데이터를 불러오는 중 오류가 발생했습니다.", "danger", "오류");
            } finally {
                this.loading = false;
            }
        },

        /** row.facilityIdx로 facilities에서 시설명 조회 (API의 facility가 null인 경우 대비) */
        getFacilityName(facilityIdx) {
            if (facilityIdx == null) return null;
            const f = this.facilities.find((x) => x.facilityIdx === facilityIdx);
            return f ? f.facilityName || f.facilityCode || null : null;
        },

        resetFilters() {
            this.filters = {
                period: this.fixedPeriod ? String(this.fixedPeriod).replace(/-/g, "") : "",
                orgIdx: null,
                scope: null,
                facilityIdx: null,
                categoryIdx: null,
                draftStat: null,
            };
            this.currentPage = 1;
            this.loadData();
        },

        editRow(row) {
            this.editMode = "edit";
            this.editingRow = { ...row };
            this.showEditModal = true;
        },

        openAddModal() {
            this.editMode = "create";
            this.editingRow = null;
            this.showEditModal = true;
            // create 모드 진입 시 configs 갱신 (fixedOrgIdx 변경 등 대비)
            if (this.fixedOrgIdx) {
                this.loadConfigs();
            }
        },

        openEvidenceModal(row) {
            this.evidenceRow = { ...row };
            this.showEvidenceModal = true;
        },

        async handleEditSave(editedData) {
            try {
                const mode = editedData?.mode || "edit";
                if (mode === "create") {
                    const payload = {
                        orgIdx: this.fixedOrgIdx || undefined,
                        configIdx: editedData.configIdx,
                        period: editedData.period || this.fixedPeriod,
                        amount: editedData.amount ?? 0,
                        draftStat: editedData.draftStat || "DRAFT",
                        dataSource:
                            editedData.dataSource === "" || editedData.dataSource === undefined
                                ? null
                                : editedData.dataSource,
                    };
                    const res1 = await carbonApi.activityData.createActivityData(payload);
                    this.showToast(res1.data.message, "success", "등록 완료");
                } else {
                    const payload = {
                        configIdx: editedData.configIdx,
                        period: editedData.period || this.fixedPeriod,
                        amount: editedData.amount,
                        draftStat: editedData.draftStat,
                        dataSource:
                            editedData.dataSource === "" || editedData.dataSource === undefined
                                ? null
                                : editedData.dataSource,
                    };
                    const res2 = await carbonApi.activityData.updateActivityData(editedData.activityIdx, payload);
                    this.showToast(res2.data.message, "success", "수정 완료");
                }
                this.showEditModal = false;
                this.loadData(); // 목록 새로고침
            } catch (error) {
                console.error("[ActivityDataViewer] 저장 실패:", error);
                const msg = error?.response?.data?.message || error?.message || "저장 중 오류가 발생했습니다.";
                this.showToast(msg, "danger", "오류");
                throw error; // EditRowModal onSave가 실패를 인식하도록 re-throw
            }
        },

        async deleteRow(row) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `기간 ${this.formatPeriod(row.period)}, ${row.category} 데이터를 삭제하시겠습니까?`,
                {
                    title: "삭제 확인",
                    okVariant: "danger",
                    okTitle: "삭제",
                    cancelTitle: "취소",
                    centered: true,
                },
            );

            if (!confirmed) return;

            try {
                await carbonApi.activityData.deleteActivityData(row.activityIdx);

                this.showToast("활동자료가 삭제되었습니다.", "success", "삭제 완료");
                this.loadData(); // 목록 새로고침
            } catch (error) {
                console.error("[ActivityDataViewer] 삭제 실패:", error);
                this.showToast("삭제 중 오류가 발생했습니다.", "danger", "오류");
            }
        },

        formatPeriod(period) {
            if (!period) return "";
            const str = String(period);
            return `${str.slice(0, 4)}-${str.slice(4, 6)}`;
        },

        formatNumber(value) {
            if (value == null) return "-";
            return Number(value).toLocaleString("ko-KR");
        },

        getDraftStatLabel(draftStat) {
            return CarbonCodes.draftStat.getLabel(draftStat) || "-";
        },

        // ─── 활동량 인라인 편집 ───
        startAmountEdit(row) {
            if (row.sourceType === "ALLOCATED") return;
            this.editingAmountRow = row.activityIdx;
            this.editingAmountValue = row.amount != null ? String(row.amount) : "";
            this.$nextTick(() => {
                const el = this.$refs.amountInput;
                const input = Array.isArray(el) ? el[0] : el;
                input?.focus();
            });
        },
        cancelAmountEdit() {
            this.editingAmountRow = null;
            this.editingAmountValue = "";
        },
        handleAmountBlur() {
            this.cancelAmountEdit();
        },
        async handleAmountSave(row) {
            if (this.editingAmountRow !== row.activityIdx) return;

            const raw = this.editingAmountValue.trim();
            const parsed = raw === "" ? null : parseFloat(raw);

            if (parsed !== null && (isNaN(parsed) || parsed < 0)) {
                this.showToast("활동량은 0 이상의 숫자를 입력해주세요.", "warning", "입력 오류");
                return;
            }

            const newAmount = parsed !== null ? parsed : 0;
            if (Number(row.amount) === newAmount) {
                this.cancelAmountEdit();
                return;
            }

            this.savingAmountIdx = row.activityIdx;
            this.editingAmountRow = null;
            this.editingAmountValue = "";

            try {
                await carbonApi.activityData.updateActivityData(row.activityIdx, { amount: newAmount });
                this.$set(row, "amount", newAmount);
                this.showToast("활동량이 수정되었습니다.", "success", "수정 완료");
            } catch (err) {
                console.error("[ActivityDataViewer] 활동량 수정 실패:", err);
                const msg = err?.response?.data?.message || err?.response?.data?.error || "활동량 수정에 실패했습니다.";
                this.showToast(msg, "danger", "오류");
                this.editingAmountRow = row.activityIdx;
                this.editingAmountValue = raw;
            } finally {
                this.savingAmountIdx = null;
            }
        },

        showToast(message, variant = "info", title = "알림") {
            this.$bvToast.toast(message, { variant, title, toaster: "b-toaster-top-right" });
        },
    },
};
</script>

<style scoped>
/* 메인 레이아웃 */
.activity-data-viewer-compact {
    padding: 10px 16px;
    height: 100%;
}

.section-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* border: 1px solid #e2e8f0;
    border-radius: 4px; */
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.filter-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    flex-shrink: 0;
}

.filter-right-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-left: auto;
}

.filter-fields-group {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.filter-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

.mx-datepicker {
    width: 210px !important;
}

.period-fixed-label {
    display: inline-block;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}

/* 섹션 블록 */
.section-block {
    background: #ffffff;
}

/* 테이블 컨테이너 */
/* table-compact 스타일은 전역 _carbon-tables.scss에서 처리 */

/* 태그 */
.tag-small {
    padding: 3px 8px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
}

.tag-small.scope-1 {
    background: #fef2f2;
    color: #dc2626;
}

.tag-small.scope-2 {
    background: #eff6ff;
    color: #2563eb;
}

.tag-small.scope-3 {
    background: #fef3c7;
    color: #d97706;
}

.tag-small.status-draft {
    background: #f3f4f6;
    color: #6b7280;
}

.tag-small.status-ACTIVE {
    background: #f0fdf4;
    color: #16a34a;
}

.tag-small.status-inactive {
    background: #fef2f2;
    color: #dc2626;
}

/* 액션 버튼 */
.icon-btn-mini {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 12px;
}

.icon-btn-mini:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
}

.icon-btn-mini.evidence:hover {
    background: #eff6ff;
    color: #2563eb;
    border-color: #2563eb;
}

.icon-btn-mini.danger:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #dc2626;
}

/* 유틸리티 */
.text-center {
    text-align: center;
    padding: 30px;
}

.empty {
    color: #94a3b8;
}

/* 활동량 열 고정 너비 (input과 셀 일치) */
.table-compact th.th-amount,
.table-compact td.amount-cell {
    width: 150px;
    min-width: 150px;
}

/* 활동량 인라인 편집 */
.amount-cell {
    cursor: pointer;
    position: relative;
}

.amount-cell.is-disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.amount-cell.is-editing {
    padding: 2px 8px;
}

.amount-display {
    display: inline-block;
}

.amount-input-inline {
    display: block;
    width: 100%;
    min-width: 70px;
    box-sizing: border-box;
    padding: 4px 8px;
    border: 1px solid #52b788;
    border-radius: 4px;
    font-size: 12px;
    text-align: right;
    background: #f0fdf4;
}

.amount-input-inline:focus {
    outline: none;
    border-color: #2d8659;
    box-shadow: 0 0 0 2px rgba(82, 183, 136, 0.2);
}

.amount-saving {
    margin-left: 6px;
    color: #52b788;
    font-size: 11px;
}

/* 반응형 */
@media (max-width: 768px) {
    .section-header-compact {
        flex-direction: column;
        align-items: stretch;
    }

    .filter-fields-group {
        flex-direction: column;
    }

    .filter-actions {
        width: 100%;
    }

    .filter-actions button {
        flex: 1;
    }

    .input-compact,
    .select-compact {
        width: 100%;
        min-width: auto;
    }
}
</style>
